import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { useAutosave, SaveState, TextField, SelectField, Field } from "@/components/Form";
import { DEV_STAGES } from "@/lib/format";

const IPQ = [
  ["technical_invention", "Does the innovation work in a new technical way (a device, process, composition or technical method)?"],
  ["software_only", "Is the novelty mainly in software, a business method or the presentation of information?"],
  ["brand", "Do you trade, or plan to trade, under a name, logo or slogan?"],
  ["appearance", "Is the shape, pattern or look of a product new and important?"],
  ["creative_works", "Have you created original code, text, artwork, photos, music or video?"],
  ["confidential", "Do you hold formulas, processes, data or methods you keep secret?"],
];

export const STEPS = [
  ["Problem and solution", [["problem", "What problem are you solving?", true], ["solution", "What is your proposed solution?", true], ["novelty", "What is new or different compared with existing solutions?", true]]],
  ["Development", [["dev_stage", "Development stage", "select"], ["prototype_status", "Prototype status", true], ["testing", "Testing carried out so far", true]]],
  ["Market", [["target_customers", "Target customers", true], ["competitors", "Competitors and alternatives", true], ["commercial_opportunity", "Commercial opportunity", true]]],
  ["Funding and timetable", [["funding_amount", "Funding amount needed (£)"], ["expenditure", "Intended expenditure", true], ["timetable", "Project timetable and milestones", true]]],
  ["People and ownership", [["contributors", "Inventors, creators and contributors (names, roles, employment status)", true], ["ownership_arrangements", "Ownership arrangements (assignments, contractor agreements, university involvement)", true]]],
  ["IP and disclosure", [["existing_ip", "Existing IP you own or license", true], ["previous_applications", "Previous IP or funding applications for this project", true]]],
];

const YesNo = ({ q, label, value, onChange }) => (
  <fieldset className="border-b border-slate-100 py-3">
    <legend className="text-sm text-slate-800">{label}</legend>
    <div className="mt-2 flex gap-2">
      {["yes", "no", "unsure"].map((v) => (
        <label key={v} className={`cursor-pointer rounded-md border px-3 py-1.5 text-xs capitalize ${value === v ? "border-[#0D9488] bg-teal-50 text-teal-800" : "border-slate-300"}`}>
          <input type="radio" name={q} value={v} checked={value === v} onChange={() => onChange(v)} className="sr-only" data-testid={`ipq-${q}-${v}`} />{v}
        </label>
      ))}
    </div>
  </fieldset>
);

export default function ProjectQuestionnaire({ project, onSaved }) {
  const [step, setStep] = useState(0);
  const save = useCallback(async (body) => { const { data } = await api.put(`/projects/${project.id}`, body); onSaved(data); }, [project.id, onSaved]);
  const { values, set, state } = useAutosave(project, save);
  const last = step === STEPS.length;
  const p = { values, set, prov: project.provenance };
  return (
    <section className="card p-6" data-testid="project-questionnaire">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="caption">Section {Math.min(step + 1, STEPS.length + 1)} of {STEPS.length + 1}: {last ? "Disclosure and IP questions" : STEPS[step][0]}</p>
        <SaveState state={state} />
      </div>
      <div className="mt-2 h-1.5 rounded bg-slate-100"><div className="h-1.5 rounded bg-[#0D9488] transition-[width]" style={{ width: `${((step + 1) / (STEPS.length + 1)) * 100}%` }} /></div>
      <div className="mt-6 space-y-5">
        {step === 0 && <><SelectField name="goal" label="What do you need help with?" options={{ ip: "Protecting my IP", funding: "Finding funding", both: "Both" }} {...p} /><p className="text-xs text-slate-500">Keep this plain English. You don't need to give confidential technical detail at this stage.</p><TextField name="title" label="Project title" {...p} /></>}
        {!last && STEPS[step][1].map(([name, label, kind]) => kind === "select" ? <SelectField key={name} name={name} label={label} options={DEV_STAGES} {...p} /> : <TextField key={name} name={name} label={label} area={kind === true} {...p} />)}
        {last && (
          <>
            <SelectField name="public_disclosure" label="Has the innovation been disclosed outside the business?" options={{ none: "No disclosure", confidential_only: "Only under NDA / confidentiality", public: "Yes - publicly (pitch, website, paper, demo, sale)" }} {...p} />
            {values.public_disclosure === "public" && <TextField name="disclosure_date" label="Date of first public disclosure" type="date" {...p} />}
            <TextField name="disclosure_details" label="Disclosure details (what, where, when, to whom)" area {...p} />
            <Field label="Protection questions">
              {IPQ.map(([q, label]) => <YesNo key={q} q={q} label={label} value={values.ip_questions?.[q]} onChange={(v) => set("ip_questions", { ...(values.ip_questions || {}), [q]: v })} />)}
            </Field>
          </>
        )}
      </div>
      <div className="mt-8 flex justify-between">
        <button disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="journey-step-back-button" className="btn-ghost btn-sm">Back</button>
        {!last && <button onClick={() => setStep(step + 1)} data-testid="journey-step-next-button" className="btn-teal btn-sm">Next section</button>}
      </div>
    </section>
  );
}
